import React from 'react';
import styled, { keyframes } from 'styled-components';
import { shouldForwardProp } from '../../utils/styled-helpers';

// 스피너 타입 정의
export type SpinnerSize = 'small' | 'medium' | 'large';
export type SpinnerVariant = 'primary' | 'secondary' | 'dark' | 'light';

// 스피너 프롭스 인터페이스
interface SpinnerProps {
  size?: SpinnerSize;
  variant?: SpinnerVariant;
  fullPage?: boolean;
  label?: string;
  className?: string;
  /**
   * 스피너가 가리키는 요소의 ID (aria-labelledby와 함께 사용)
   */
  ariaLabelledby?: string;
  /**
   * 스크린 리더용 설명
   */
  ariaLabel?: string;
  /**
   * 스피너가 스크린 리더에서 숨겨야 하는지 여부
   */
  ariaHidden?: boolean;
}

// 스피너 크기 계산 함수
const getSpinnerSize = (size: SpinnerSize): string => {
  switch (size) {
    case 'small':
      return '16px';
    case 'large':
      return '48px';
    case 'medium':
    default:
      return '32px';
  }
};

// 스피너 색상 계산 함수
const getSpinnerColor = (variant: SpinnerVariant): string => {
  switch (variant) {
    case 'secondary':
      return '#6c757d';
    case 'dark':
      return '#343a40';
    case 'light':
      return '#f8f9fa';
    case 'primary':
    default:
      return '#4a6cfa';
  }
};

// 스피너 애니메이션
const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

// 스피너 컨테이너
const SpinnerContainer = styled.div.withConfig({
  shouldForwardProp: shouldForwardProp(['fullPage']),
})<{ fullPage: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  ${({ fullPage }) =>
    fullPage &&
    `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.7);
    z-index: 9999;
  `}
`;

// 스피너 스타일
const SpinnerElement = styled.div.withConfig({
  shouldForwardProp: shouldForwardProp(['size', 'variant']),
})<{
  size: SpinnerSize;
  variant: SpinnerVariant;
}>`
  width: ${({ size }) => getSpinnerSize(size)};
  height: ${({ size }) => getSpinnerSize(size)};
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: ${({ variant }) => getSpinnerColor(variant)};
  animation: ${spin} 0.8s linear infinite;
`;

// 스피너 레이블
const SpinnerLabel = styled.p.withConfig({
  shouldForwardProp: shouldForwardProp(['size']),
})<{
  size: SpinnerSize;
}>`
  margin-top: ${({ size }) => (size === 'small' ? '0.5rem' : '1rem')};
  color: var(--ws-ink-3);
  font-size: ${({ size }) => (size === 'small' ? '0.75rem' : '0.875rem')};
`;

/**
 * 스피너 컴포넌트 - 로딩 상태를 표시
 *
 * @param {SpinnerSize} size - 스피너 크기 ('small', 'medium', 'large')
 * @param {SpinnerVariant} variant - 스피너 색상 ('primary', 'secondary', 'dark', 'light')
 * @param {boolean} fullPage - 전체 페이지를 덮는 스피너 여부
 * @param {string} label - 스피너 아래 표시할 텍스트
 * @param {string} className - 추가 CSS 클래스
 * @param {string} ariaLabelledby - 스피너가 가리키는 요소의 ID
 * @param {string} ariaLabel - 스크린 리더용 설명
 * @param {boolean} ariaHidden - 스크린 리더에서 숨김 여부
 */
const Spinner: React.FC<SpinnerProps> = ({
  size = 'medium',
  variant = 'primary',
  fullPage = false,
  label,
  className,
  ariaLabelledby,
  ariaLabel,
  ariaHidden,
}) => {
  // 기본 aria-label을 설정 (label이 있으면 그것을 사용, 없으면 '로딩 중')
  const defaultAriaLabel = label || '로딩 중';

  return (
    <SpinnerContainer
      fullPage={fullPage}
      className={className}
      role="status"
      aria-live="polite"
      aria-busy={true}
    >
      <SpinnerElement
        size={size}
        variant={variant}
        aria-hidden={ariaHidden !== undefined ? ariaHidden : true}
      />
      {label && (
        <SpinnerLabel size={size} id={ariaLabelledby ? undefined : 'spinner-label'}>
          {label}
        </SpinnerLabel>
      )}
      {/* 화면에 표시되지 않는 접근성용 텍스트 */}
      <span className="sr-only" aria-live="assertive">
        {ariaLabel || defaultAriaLabel}
      </span>
    </SpinnerContainer>
  );
};

// displayName 속성 추가
Spinner.displayName = 'Spinner';

export default Spinner;
